import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { eventAPI } from '../../lib/eventApi';
import {
  Trash, ArrowUp, ArrowDown, Lock, LockOpen, Play,
  UserPlus, MagnifyingGlass, UserCirclePlus, Key,
} from '@phosphor-icons/react';
import ConfirmDialog from '../ConfirmDialog';
import StartSessionDialog from './StartSessionDialog';
import ManualPlayerDialog from './ManualPlayerDialog';
import ClaimLinkDialog from './ClaimLinkDialog';
import SharedPartnerRow from './SharedPartnerRow';

const PRE_START = ['draft', 'registration_open', 'registration_closed'];
const PLACEHOLDER_DOMAIN = '@placeholder.local';

const isManualProfile = (p) => {
  if (!p) return false;
  if (p.created_manually === true) return true;
  return !!p.email && p.email.toLowerCase().endsWith(PLACEHOLDER_DOMAIN);
};

export default function RegistrationSetupTab({ session, registrations, onChange, readOnly = false, isAdmin = false }) {
  const [allPlayers, setAllPlayers] = useState([]);
  const [search, setSearch] = useState('');
  const [addingRole, setAddingRole] = useState('player');
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [confirmStart, setConfirmStart] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [claimTarget, setClaimTarget] = useState(null);
  const sessionId = session.id;
  const editable = !readOnly && PRE_START.includes(session.status);
  // Phase A — Sub Pool management is allowed AFTER start as well. Active
  // player slots stay locked (would corrupt assignments/standings), but
  // adding / removing subs is safe and explicitly supported by the
  // backend. `subsEditable` covers both pre-start (same as editable)
  // and the post-start admin/EM-only window.
  const isStarted = !PRE_START.includes(session.status);
  const subsEditable = !readOnly;

  useEffect(() => {
    eventAPI.listAvailablePlayers(sessionId)
      .then((r) => setAllPlayers(r.data?.profiles || []))
      .catch(() => setAllPlayers([]));
  }, [sessionId]);

  // When the session has started, force addingRole to 'sub' — the backend
  // also rejects post-start 'player' additions, but locking the UI prevents
  // user confusion.
  useEffect(() => {
    if (isStarted && addingRole !== 'sub') setAddingRole('sub');
  }, [isStarted, addingRole]);

  const registeredProfileIds = useMemo(
    () => new Set(registrations.map((r) => r.profile_id)),
    [registrations],
  );

  const players = useMemo(
    () => registrations.filter((r) => r.role === 'player'),
    [registrations],
  );
  const subs = useMemo(
    () => registrations.filter((r) => r.role === 'sub'),
    [registrations],
  );

  const expectedPlayers = session.num_courts * session.players_per_court;

  // ranks must be a contiguous 1..N permutation
  const sortedRanks = [...players.map((p) => p.initial_rank)].filter((r) => r != null).sort((a, b) => a - b);
  const ranksValid =
    players.length === expectedPlayers &&
    sortedRanks.length === expectedPlayers &&
    sortedRanks.every((r, i) => r === i + 1);

  const filteredCandidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allPlayers
      .filter((p) => !registeredProfileIds.has(p.profile_id || p.id))
      .filter((p) => {
        if (!q) return true;
        const n = (p.name || '').toLowerCase();
        return n.includes(q);
      })
      .slice(0, 20);
  }, [allPlayers, registeredProfileIds, search]);

  const addPlayer = async (cand) => {
    if (!editable && !(subsEditable && addingRole === 'sub')) return;
    setBusy(true);
    try {
      const nextRank =
        addingRole === 'player'
          ? (players.reduce((m, p) => Math.max(m, p.initial_rank || 0), 0) + 1)
          : null;
      await eventAPI.addRegistration(sessionId, {
        profile_id: cand.profile_id || cand.id,
        role: addingRole,
        initial_rank: nextRank,
      });
      toast.success(`${cand.name} added as ${addingRole}`);
      setSearch('');
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add player');
    } finally {
      setBusy(false);
    }
  };

  // Manual-player flow — admin creates a new participant and we immediately
  // register them as the currently selected role. We also append the new
  // profile to the local candidate list so the picker stays useful even if
  // the admin wants to add another one.
  const onManualCreated = async (profile) => {
    setAllPlayers((prev) => [
      ...prev,
      { profile_id: profile.profile_id, name: profile.name },
    ]);
    await addPlayer({ profile_id: profile.profile_id, name: profile.name });
  };

  const removeReg = async (reg) => {
    setBusy(true);
    try {
      await eventAPI.deleteRegistration(sessionId, reg.id);
      // After removal, normalize player ranks to 1..N to avoid gaps
      await renumberRanks(
        players.filter((p) => p.id !== reg.id).sort(
          (a, b) => (a.initial_rank ?? 9999) - (b.initial_rank ?? 9999),
        ),
      );
      toast.success('Removed');
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove');
    } finally {
      setBusy(false);
      setConfirmRemove(null);
    }
  };

  const renumberRanks = async (sortedPlayers) => {
    // Quietly PATCH each registration to its new sequential rank.
    await Promise.all(
      sortedPlayers.map((p, idx) => {
        const newRank = idx + 1;
        if (p.initial_rank === newRank) return Promise.resolve();
        return eventAPI.updateRegistration(sessionId, p.id, { initial_rank: newRank });
      }),
    );
  };

  const moveRank = async (reg, delta) => {
    if (!editable) return;
    const sorted = [...players].sort(
      (a, b) => (a.initial_rank ?? 9999) - (b.initial_rank ?? 9999),
    );
    const idx = sorted.findIndex((p) => p.id === reg.id);
    if (idx < 0) return;
    const swapIdx = idx + delta;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[swapIdx];
    setBusy(true);
    try {
      await eventAPI.updateRegistration(sessionId, a.id, { initial_rank: b.initial_rank });
      await eventAPI.updateRegistration(sessionId, b.id, { initial_rank: a.initial_rank });
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reorder');
    } finally {
      setBusy(false);
    }
  };

  const promoteToPlayer = async (reg) => {
    if (!editable) return;
    if (players.length >= expectedPlayers) {
      toast.error(`Roster is full (${expectedPlayers} players)`);
      return;
    }
    setBusy(true);
    try {
      const nextRank = players.reduce((m, p) => Math.max(m, p.initial_rank || 0), 0) + 1;
      await eventAPI.updateRegistration(sessionId, reg.id, { role: 'player', initial_rank: nextRank });
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not promote');
    } finally {
      setBusy(false);
    }
  };

  const demoteToSub = async (reg) => {
    if (!editable) return;
    setBusy(true);
    try {
      await eventAPI.updateRegistration(sessionId, reg.id, { role: 'sub', initial_rank: null });
      // Renumber the remaining players
      const remaining = players
        .filter((p) => p.id !== reg.id)
        .sort((a, b) => (a.initial_rank ?? 9999) - (b.initial_rank ?? 9999));
      await renumberRanks(remaining);
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not demote');
    } finally {
      setBusy(false);
    }
  };

  const flipRegistrationStatus = async (newStatus) => {
    setBusy(true);
    try {
      await eventAPI.updateSession(sessionId, { status: newStatus });
      toast.success(newStatus === 'registration_open' ? 'Registration opened' : 'Registration closed');
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change status');
    } finally {
      setBusy(false);
    }
  };

  const patchSession = async (patch, label) => {
    setBusy(true);
    try {
      await eventAPI.updateSession(sessionId, patch);
      toast.success(label || 'Saved');
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const startSession = async () => {
    setBusy(true);
    try {
      await eventAPI.startSession(sessionId);
      toast.success('Session started');
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start session');
    } finally {
      setBusy(false);
      setConfirmStart(false);
    }
  };

  // Court assignment preview — pure client-side
  const courtPreview = useMemo(() => {
    if (!ranksValid) return [];
    const sorted = [...players].sort(
      (a, b) => (a.initial_rank ?? 9999) - (b.initial_rank ?? 9999),
    );
    const out = [];
    for (let c = 0; c < session.num_courts; c++) {
      out.push({
        court_number: c + 1,
        slots: sorted.slice(c * session.players_per_court, (c + 1) * session.players_per_court),
      });
    }
    return out;
  }, [ranksValid, players, session.num_courts, session.players_per_court]);

  return (
    <div className="space-y-6" data-testid="setup-tab">
      {/* Registration status controls */}
      <section className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 space-y-3" data-testid="registration-status-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Registration window</div>
            <div className="text-sm text-[#013523] font-medium" data-testid="session-status">
              {session.status.replace(/_/g, ' ')}
            </div>
          </div>
          {editable && (
            <div className="flex items-center gap-2">
              {session.status !== 'registration_open' && (
                <button
                  onClick={() => flipRegistrationStatus('registration_open')}
                  disabled={busy}
                  data-testid="open-registration-btn"
                  className="px-3 py-1.5 rounded-lg text-xs border border-[#015e3f] text-[#015e3f] hover:bg-[#015e3f]/5 disabled:opacity-50 flex items-center gap-1"
                >
                  <LockOpen size={14} /> Open
                </button>
              )}
              {session.status !== 'registration_closed' && (
                <button
                  onClick={() => flipRegistrationStatus('registration_closed')}
                  disabled={busy}
                  data-testid="close-registration-btn"
                  className="px-3 py-1.5 rounded-lg text-xs border border-[#e3dcd1] text-[#6a766e] hover:bg-[#e3dcd1]/40 disabled:opacity-50 flex items-center gap-1"
                >
                  <Lock size={14} /> Close
                </button>
              )}
            </div>
          )}
        </div>
        <div className="text-xs text-[#6a766e]">
          Players: <span className="font-medium text-[#013523]">{players.length} / {expectedPlayers}</span> · Subs: <span className="font-medium text-[#013523]">{subs.length}</span>
        </div>
      </section>

      {/* Schedule & access — read-only summary. Editing dates happens in the
          Edit Session modal (top-right). Replacing the native datetime-local
          inputs avoids the iOS Safari intrinsic-min-width collision that
          kept making this card look broken on iPad widths regardless of
          grid breakpoint. */}
      <section className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 space-y-3" data-testid="schedule-access-card">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Schedule &amp; access</div>
          <div className="text-[11px] text-[#6a766e]">Edit dates via the <span className="text-[#015e3f]">Edit</span> button</div>
        </div>
        <dl className="divide-y divide-[#e3dcd1] border border-[#e3dcd1] rounded-lg overflow-hidden bg-white">
          <div className="px-3 py-2 flex items-baseline justify-between gap-3" data-testid="setup-starts-at-row">
            <dt className="text-[11px] uppercase tracking-wider text-[#6a766e]">Starts at</dt>
            <dd className="text-sm text-[#013523] text-right" data-testid="setup-starts-at-value">
              {formatPretty(session.starts_at) || 'Not set'}
            </dd>
          </div>
          <div className="px-3 py-2 flex items-baseline justify-between gap-3" data-testid="setup-closes-at-row">
            <dt className="text-[11px] uppercase tracking-wider text-[#6a766e]">Registration closes</dt>
            <dd className="text-sm text-[#013523] text-right" data-testid="setup-closes-at-value">
              {formatPretty(session.registration_closes_at) || 'Not set'}
            </dd>
          </div>
        </dl>
        <label className="flex items-start gap-2 cursor-pointer" data-testid="setup-allow-self-register-toggle">
          <input
            type="checkbox"
            disabled={!editable}
            checked={!!session.allow_self_register}
            onChange={(e) => patchSession({ allow_self_register: e.target.checked }, e.target.checked ? 'Self-registration enabled' : 'Self-registration disabled')}
            className="mt-0.5 accent-[#015e3f]"
          />
          <div className="text-xs">
            <div className="font-medium text-[#013523]">Allow self-registration</div>
            <div className="text-[#6a766e]">
              When OFF, only admins can add players. When ON, approved members can self-register while status is open.
            </div>
          </div>
        </label>
      </section>

      {/* Add player */}
      {(editable || (subsEditable && isStarted)) && (
        <section className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 space-y-3" data-testid="add-player-card">
          <div className="flex items-center gap-2">
            <UserPlus size={18} className="text-[#015e3f]" />
            <div className="font-medium text-[#013523]">
              {isStarted ? 'Add to Sub Pool' : 'Add to session'}
            </div>
          </div>
          {!isStarted && (
            <div className="flex gap-2">
              {['player', 'sub'].map((r) => (
                <button
                  key={r}
                  onClick={() => setAddingRole(r)}
                  data-testid={`add-as-${r}`}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-xs border transition ${
                    addingRole === r
                      ? 'bg-[#015e3f] text-white border-[#015e3f]'
                      : 'bg-[#fbfaf8] text-[#013523] border-[#e3dcd1]'
                  }`}
                >
                  {r === 'player' ? 'Add as player' : 'Add as sub'}
                </button>
              ))}
            </div>
          )}
          {isStarted && (
            <p className="text-[11px] text-[#6a766e]" data-testid="add-sub-only-hint">
              Session has started — you can still add subs. Player roster is locked.
            </p>
          )}
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6a766e]" />
            <input
              data-testid="player-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search ladder players…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#e3dcd1] bg-white text-sm"
            />
          </div>
          {(search || filteredCandidates.length > 0) && (
            <ul className="max-h-60 overflow-y-auto divide-y divide-[#e3dcd1] border border-[#e3dcd1] rounded-lg" data-testid="candidate-list">
              {filteredCandidates.length === 0 && (
                <li className="px-3 py-2 text-xs text-[#6a766e]">No matches</li>
              )}
              {filteredCandidates.map((p) => (
                <li
                  key={p.profile_id || p.id}
                  className="px-3 py-2 flex items-center justify-between gap-2 hover:bg-[#f0ece3]/40 cursor-pointer"
                  data-testid={`candidate-${p.profile_id || p.id}`}
                  onClick={() => addPlayer(p)}
                >
                  <div className="min-w-0">
                    <div className="text-sm text-[#013523] truncate">{p.name}</div>
                  </div>
                  <button
                    disabled={busy}
                    className="text-xs px-2 py-1 rounded-md border border-[#015e3f] text-[#015e3f] disabled:opacity-50"
                  >
                    Add
                  </button>
                </li>
              ))}
            </ul>
          )}
          {/* Manual-player creation — admin only. Lets the admin add a player
              who hasn't signed up yet, then immediately registers them for
              the session as the currently selected role. */}
          {isAdmin && (
            <div className="pt-2 border-t border-[#e3dcd1]">
              <button
                type="button"
                onClick={() => setManualOpen(true)}
                disabled={busy}
                data-testid="open-manual-player-btn"
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs border border-dashed border-[#015e3f] text-[#015e3f] hover:bg-[#015e3f]/5 disabled:opacity-50"
              >
                <UserCirclePlus size={14} weight="bold" />
                Can&apos;t find them? Create a manual player
              </button>
            </div>
          )}
        </section>
      )}

      {/* Players list */}
      <section className="space-y-2" data-testid="players-list">
        <h3 className="font-medium text-[#013523]">Players ({players.length}/{expectedPlayers})</h3>
        {players.length === 0 && (
          <p className="text-xs text-[#6a766e]">No players registered yet.</p>
        )}
        {[...players]
          .sort((a, b) => (a.initial_rank ?? 9999) - (b.initial_rank ?? 9999))
          .map((p, idx, arr) => (
            <div
              key={p.id}
              data-testid={`player-row-${p.id}`}
              className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-xl px-3 py-2 flex items-center gap-2"
            >
              <div className="w-7 text-center text-sm font-medium text-[#015e3f]" data-testid={`player-rank-${p.id}`}>
                {p.initial_rank ?? '—'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#013523] truncate flex items-center gap-1.5">
                  <span className="truncate">{p.full_name || '—'}</span>
                  {isManualProfile(p) && (
                    <span
                      data-testid={`manual-badge-${p.id}`}
                      className="shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#fff5e6] text-[#a8580f] border border-[#f4d29c]"
                    >
                      Manual
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-[#6a766e] truncate">
                  {isManualProfile(p) && p.email?.toLowerCase().endsWith(PLACEHOLDER_DOMAIN)
                    ? 'No email set yet'
                    : p.email}
                </div>
                {/* Shared Session (Phase B) — admin/EM can attach/edit/
                    remove pre AND post start. Hidden in readOnly views. */}
                {!readOnly && (
                  <SharedPartnerRow
                    sessionId={sessionId}
                    registrationId={p.id}
                    sharedPartner={p.shared_partner}
                    onChange={onChange}
                  />
                )}
              </div>
              {isAdmin && isManualProfile(p) && (
                <button
                  onClick={() => setClaimTarget({
                    profile_id: p.profile_id,
                    full_name: p.full_name,
                    email: p.email,
                  })}
                  data-testid={`player-claim-${p.id}`}
                  title="Generate claim link"
                  className="px-2 py-1 text-[11px] rounded-md border border-[#015e3f] text-[#015e3f] hover:bg-[#015e3f]/5 flex items-center gap-1"
                >
                  <Key size={12} weight="bold" />
                  Claim
                </button>
              )}
              {editable && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveRank(p, -1)}
                    disabled={busy || idx === 0}
                    data-testid={`player-up-${p.id}`}
                    className="p-1.5 rounded-md border border-[#e3dcd1] text-[#6a766e] hover:text-[#013523] disabled:opacity-40"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    onClick={() => moveRank(p, +1)}
                    disabled={busy || idx === arr.length - 1}
                    data-testid={`player-down-${p.id}`}
                    className="p-1.5 rounded-md border border-[#e3dcd1] text-[#6a766e] hover:text-[#013523] disabled:opacity-40"
                  >
                    <ArrowDown size={14} />
                  </button>
                  <button
                    onClick={() => demoteToSub(p)}
                    disabled={busy}
                    data-testid={`player-demote-${p.id}`}
                    className="px-2 py-1 text-[11px] rounded-md border border-[#e3dcd1] text-[#6a766e] hover:text-[#013523]"
                  >
                    To sub
                  </button>
                  <button
                    onClick={() => setConfirmRemove(p)}
                    disabled={busy}
                    data-testid={`player-remove-${p.id}`}
                    className="p-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
      </section>

      {/* Subs list */}
      <section className="space-y-2" data-testid="subs-list">
        <h3 className="font-medium text-[#013523]">Subs ({subs.length})</h3>
        {subs.length === 0 && (
          <p className="text-xs text-[#6a766e]">No subs added.</p>
        )}
        {subs.map((s) => (
          <div
            key={s.id}
            data-testid={`sub-row-${s.id}`}
            className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-xl px-3 py-2 flex items-center gap-2"
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[#013523] truncate flex items-center gap-1.5">
                <span className="truncate">{s.full_name || '—'}</span>
                {isManualProfile(s) && (
                  <span
                    data-testid={`manual-badge-${s.id}`}
                    className="shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#fff5e6] text-[#a8580f] border border-[#f4d29c]"
                  >
                    Manual
                  </span>
                )}
              </div>
              <div className="text-[11px] text-[#6a766e] truncate">
                {isManualProfile(s) && s.email?.toLowerCase().endsWith(PLACEHOLDER_DOMAIN)
                  ? 'No email set yet'
                  : s.email}
              </div>
            </div>
            {isAdmin && isManualProfile(s) && (
              <button
                onClick={() => setClaimTarget({
                  profile_id: s.profile_id,
                  full_name: s.full_name,
                  email: s.email,
                })}
                data-testid={`sub-claim-${s.id}`}
                title="Generate claim link"
                className="px-2 py-1 text-[11px] rounded-md border border-[#015e3f] text-[#015e3f] hover:bg-[#015e3f]/5 flex items-center gap-1"
              >
                <Key size={12} weight="bold" />
                Claim
              </button>
            )}
            {subsEditable && (
              <div className="flex items-center gap-1">
                {!isStarted && (
                  <button
                    onClick={() => promoteToPlayer(s)}
                    disabled={busy || players.length >= expectedPlayers}
                    data-testid={`sub-promote-${s.id}`}
                    className="px-2 py-1 text-[11px] rounded-md border border-[#015e3f] text-[#015e3f] disabled:opacity-40"
                  >
                    To player
                  </button>
                )}
                <button
                  onClick={() => setConfirmRemove(s)}
                  disabled={busy}
                  data-testid={`sub-remove-${s.id}`}
                  className="p-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40"
                >
                  <Trash size={14} />
                </button>
              </div>
            )}
          </div>
        ))}
      </section>

      {/* Court assignment preview */}
      <section className="space-y-2" data-testid="court-preview">
        <h3 className="font-medium text-[#013523]">Week 1 court preview</h3>
        {!ranksValid ? (
          <p className="text-xs text-[#6a766e]">
            Preview becomes available when there are exactly {expectedPlayers} players with ranks 1–{expectedPlayers}.
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {courtPreview.map((c) => (
              <div
                key={c.court_number}
                data-testid={`court-preview-${c.court_number}`}
                className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-xl p-3"
              >
                <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Court {c.court_number}</div>
                <ul className="mt-1 space-y-0.5">
                  {c.slots.map((s) => (
                    <li key={s.id} className="text-sm text-[#013523] flex justify-between gap-2">
                      <span className="truncate">{s.full_name || '—'}</span>
                      <span className="text-[#015e3f] font-medium">#{s.initial_rank}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Start session */}
      {editable && (
        <section className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4" data-testid="start-card">
          <div className="flex flex-col gap-2">
            <div className="text-sm text-[#013523] font-medium">Start session</div>
            <div className="text-xs text-[#6a766e]">
              First registered players get active roster priority.
              Initial rank determines court placement after the active roster is selected.
              Overflow players become subs when the session starts.
            </div>
            <button
              disabled={busy}
              onClick={() => setConfirmStart(true)}
              data-testid="start-session-btn"
              className="self-start px-4 py-2 rounded-lg bg-[#015e3f] text-white font-medium text-sm flex items-center gap-2 disabled:opacity-50"
            >
              <Play size={16} weight="fill" /> Review &amp; start
            </button>
          </div>
        </section>
      )}

      <ConfirmDialog
        open={!!confirmRemove}
        title="Remove registration?"
        message={confirmRemove ? `Remove ${confirmRemove.full_name || 'this player'} from the session?` : ''}
        confirmLabel="Remove"
        destructive
        onConfirm={() => removeReg(confirmRemove)}
        onCancel={() => setConfirmRemove(null)}
      />
      <StartSessionDialog
        open={confirmStart}
        session={session}
        onClose={() => setConfirmStart(false)}
        onStarted={() => {
          setConfirmStart(false);
          onChange?.();
        }}
      />
      <ManualPlayerDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onCreated={onManualCreated}
      />
      <ClaimLinkDialog
        open={!!claimTarget}
        profile={claimTarget}
        onClose={() => setClaimTarget(null)}
      />
    </div>
  );
}

// Convert an ISO string from Supabase into the local datetime-local input value.
function isoToLocalInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const tzOffsetMs = d.getTimezoneOffset() * 60 * 1000;
    return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 16);
  } catch { return ''; }
}

// Human-readable date/time used by the read-only Schedule & Access card.
// e.g. "Jun 17, 2026 at 6:30 PM" — keeps the inline summary compact while
// matching the visual the user requested.
function formatPretty(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${date} at ${time}`;
  } catch { return ''; }
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-[#6a766e] mb-1">{label}</span>
      {children}
    </label>
  );
}

